import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Card, 
  CardBody, 
  Button, 
  Divider,
  Chip
} from "@heroui/react";
import {
  FaArrowRight,
  FaPhoneAlt,
  FaFileAlt,
  FaCheckCircle,
  FaLaptopCode,
  FaShieldAlt
} from 'react-icons/fa';

// Uniwersalny komponent CTA, który można umieścić między różnymi sekcjami
// z możliwością dostosowania treści i wyglądu
const CtaSection = ({ 
  variant = 'primary', 
  title = "IT For Everyone - technologia przyjazna dla Twojego biznesu", 
  subtitle = "Skontaktuj się z nami, aby omówić Twoje potrzeby i otrzymać bezpłatną konsultację", 
  buttonText = "Skontaktuj się z nami",
  buttonLink = "/kontakt",
  features = [],
  showStats = false,
  withTestimonial = false,
  compact = false
}) => {
  // Warianty kolorystyczne
  const variants = {
    primary: {
      bgGradient: 'from-primary/10 to-secondary/10',
      border: 'border-primary/20',
      buttonColor: 'primary',
      icon: <FaLaptopCode className="text-primary text-xl" />
    },
    success: {
      bgGradient: 'from-success/10 to-success/5',
      border: 'border-success/20',
      buttonColor: 'success',
      icon: <FaCheckCircle className="text-success text-xl" />
    },
    secondary: {
      bgGradient: 'from-secondary/10 to-primary/5',
      border: 'border-secondary/20',
      buttonColor: 'secondary',
      icon: <FaShieldAlt className="text-secondary text-xl" />
    }
  };

  // Statystyki - zmniejszone wartości dla mniejszej firmy
  const stats = [
    { value: '50+', label: 'Zadowolonych klientów' },
    { value: '7+', label: 'Lata doświadczenia' },
    { value: '100%', label: 'Klientów poleca nas' }
  ];
  
  // Przykładowa opinia
  const testimonial = {
    content: "ITFE wyjaśnili naszej firmie wszystkie zagadnienia IT w prosty, przystępny sposób. Teraz wreszcie rozumiemy, czego potrzebujemy!",
    author: "Marek Nowak",
    position: "Właściciel, MałaFirma Sp. z o.o."
  };

  // Animacja
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 50, damping: 20 }
    }
  };

  // Określenie aktualnego wariantu
  const currentVariant = variants[variant] || variants.primary;

  // Jeśli nie przekazano cech, użyj domyślnych
  const defaultFeatures = [
    "Zrozumiałe rozwiązania IT", 
    "Indywidualne podejście", 
    "Przystępne ceny", 
    "Wsparcie na każdym etapie"
  ];

  const displayFeatures = features.length > 0 ? features : defaultFeatures;

  return (
    <div className="py-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Tło z gradientem */}
      <div className="absolute inset-0 bg-default-50 opacity-50" />
      
      {/* Elementy dekoracyjne */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      <div className="absolute -top-20 -left-20 w-64 h-64 bg-primary/5 rounded-full filter blur-3xl" />
      <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-secondary/5 rounded-full filter blur-3xl" />
      
      <div className="container mx-auto relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <Card className={`shadow-xl border bg-gradient-to-br ${currentVariant.bgGradient} ${currentVariant.border} overflow-hidden`}>
            <CardBody className={`${compact ? 'py-8' : 'py-12'} px-6 md:px-8`}>
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
                <div className="lg:col-span-3">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="rounded-full bg-background/80 backdrop-blur-sm p-3">
                      {currentVariant.icon}
                    </div>
                    <motion.div variants={itemVariants}>
                      <Chip color={currentVariant.buttonColor} variant="flat" size="sm">
                        IT For Everyone
                      </Chip>
                    </motion.div>
                  </div>
                  
                  <motion.h3 
                    className="text-2xl md:text-3xl font-bold mb-4" 
                    variants={itemVariants}
                  >
                    {title}
                  </motion.h3>
                  
                  <motion.p 
                    className="text-foreground-muted mb-6 max-w-2xl" 
                    variants={itemVariants}
                  >
                    {subtitle}
                  </motion.p>
                  
                  {/* Lista korzyści/cech */}
                  {displayFeatures.length > 0 && (
                    <motion.div 
                      className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" 
                      variants={itemVariants}
                    >
                      {displayFeatures.map((feature, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <FaCheckCircle className={`text-${currentVariant.buttonColor} flex-shrink-0`} />
                          <span className="text-sm">{feature}</span>
                        </div>
                      ))}
                    </motion.div>
                  )}
                  
                  {/* Statystyki */}
                  {showStats && (
                    <motion.div 
                      className="flex flex-wrap gap-6 mb-6" 
                      variants={itemVariants}
                    >
                      {stats.map((stat, index) => (
                        <div key={index} className="flex flex-col items-center">
                          <span className={`text-2xl font-bold text-${currentVariant.buttonColor}`}>
                            {stat.value}
                          </span>
                          <span className="text-xs text-foreground-muted">
                            {stat.label}
                          </span>
                        </div>
                      ))}
                    </motion.div>
                  )}
                  
                  {/* Przyciski akcji */}
                  <motion.div 
                    className="flex flex-col sm:flex-row gap-3 mt-6" 
                    variants={itemVariants}
                  >
                    <Button 
                      as={Link} 
                      to={buttonLink}
                      color={currentVariant.buttonColor}
                      size="lg"
                      endContent={<FaArrowRight />}
                    >
                      {buttonText}
                    </Button>
                    
                    <Button 
                      as="a"
                      href="tel:+48690481963"
                      variant="bordered"
                      color={currentVariant.buttonColor}
                      size="lg"
                      startContent={<FaPhoneAlt />}
                    >
                      Zadzwoń do nas
                    </Button>
                  </motion.div>
                </div>
                
                {/* Prawa kolumna - opinia lub dodatkowe elementy */}
                <div className="lg:col-span-2">
                  {withTestimonial ? (
                    <motion.div 
                      className="bg-background/80 backdrop-blur-sm p-6 rounded-lg border border-divider shadow-lg" 
                      variants={itemVariants}
                    >
                      <div className="flex flex-col h-full">
                        <div className="mb-4">
                          <svg width="42" height="30" viewBox="0 0 42 30" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-primary/20">
                            <path d="M11.7984 0.198486C5.8304 0.198486 0.998047 5.03889 0.998047 10.9929C0.998047 16.2729 5.1904 20.5149 10.3104 21.5289C14.7984 22.3289 19.1264 21.5449 22.2624 19.4369C22.5184 19.2729 22.7104 19.0249 22.8144 18.7289C22.9184 18.4329 22.9264 18.1129 22.8384 17.8089C22.7504 17.5069 22.5704 17.2449 22.3232 17.0649C22.0752 16.8849 21.7744 16.7969 21.4624 16.8129C21.1504 16.8289 20.8544 16.9369 20.6104 17.1289C18.1504 18.7769 14.7304 19.3689 11.1984 18.7529C7.3824 18.0449 4.1984 14.9689 4.1984 10.9929C4.1984 6.77889 7.6144 3.39889 11.7984 3.39889C16.0064 3.39889 19.3984 6.77889 19.3984 10.9929C19.3984 11.8369 19.0384 12.2289 18.3504 12.2289C17.7024 12.2289 17.0384 11.7489 17.0384 10.6969V5.00689C17.0384 4.51889 16.8464 4.05089 16.5024 3.70689C16.1584 3.36289 15.6944 3.17089 15.2024 3.16889C14.7104 3.16689 14.2384 3.35889 13.8944 3.69889C13.5504 4.03889 13.3504 4.51089 13.3504 5.00289V6.39889C12.1664 5.21489 10.7424 4.59889 9.1984 4.59889C5.9744 4.59889 3.3504 7.22289 3.3504 10.4169C3.3504 13.6089 5.9744 16.2329 9.1984 16.2329C11.2464 16.2329 13.0464 15.1889 14.0624 13.5289C14.7744 15.1889 16.3824 16.2289 18.2224 16.2289C21.0864 16.2289 22.5984 14.3889 22.5984 11.1129C22.5984 4.98289 17.8064 0.198486 11.7984 0.198486ZM9.1984 13.0009C7.7584 13.0009 6.5984 11.8409 6.5984 10.4169C6.5984 8.99089 7.7584 7.83089 9.1984 7.83089C10.6224 7.83089 11.7984 8.99089 11.7984 10.4169C11.7984 11.8409 10.6224 13.0009 9.1984 13.0009Z" fill="currentColor"/>
                            <path d="M40.9263 17.0648C40.6783 16.8848 40.3775 16.7968 40.0655 16.8128C39.7535 16.8288 39.4575 16.9368 39.2135 17.1288C36.7535 18.7768 33.3335 19.3688 29.8015 18.7528C25.9855 18.0448 22.8015 14.9688 22.8015 10.9928C22.8015 6.77276 26.2175 3.39276 30.4015 3.39276C34.6095 3.39276 38.0015 6.77276 38.0015 10.9928C38.0015 11.8368 37.6415 12.2288 36.9535 12.2288C36.3055 12.2288 35.6415 11.7488 35.6415 10.6968V5.00676C35.6415 4.51876 35.4495 4.05076 35.1055 3.70676C34.7615 3.36276 34.2975 3.17076 33.8055 3.16676C33.3135 3.16476 32.8415 3.35676 32.4975 3.69676C32.1535 4.03676 31.9535 4.50876 31.9535 5.00276V6.39276C30.7695 5.20876 29.3455 4.59276 27.8015 4.59276C24.5775 4.59276 21.9535 7.21676 21.9535 10.4108C21.9535 13.6028 24.5775 16.2268 27.8015 16.2268C29.8495 16.2268 31.6495 15.1828 32.6655 13.5228C33.3775 15.1828 34.9855 16.2228 36.8255 16.2228C39.6895 16.2228 41.2015 14.3828 41.2015 11.1068C41.2015 5.23276 36.5855 0.622756 30.8015 0.198756C24.8335 0.198756 20.0015 5.03676 20.0015 10.9928C20.0015 16.2728 24.1935 20.5148 29.3135 21.5288C33.8015 22.3288 38.1295 21.5448 41.2655 19.4368C41.5215 19.2728 41.7135 19.0248 41.8175 18.7288C41.9215 18.4328 41.9295 18.1128 41.8415 17.8088C41.7535 17.5068 41.5735 17.2448 41.3263 17.0648Z" fill="currentColor"/>
                          </svg>
                        </div>
                        
                        <p className="text-foreground-muted italic mb-6 flex-grow">
                          {testimonial.content}
                        </p>
                        
                        <div className="flex items-center mt-auto">
                          <div className="h-10 w-10 rounded-full bg-default-200 flex items-center justify-center mr-3">
                            <span className="text-sm font-medium">
                              {testimonial.author.split(' ').map(part => part[0]).join('')}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-sm">{testimonial.author}</p>
                            <p className="text-foreground-muted text-xs">{testimonial.position}</p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div 
                      className="bg-background/80 backdrop-blur-sm p-6 rounded-lg border border-divider shadow-lg flex flex-col h-full"
                      variants={itemVariants}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="rounded-full bg-foreground/10 p-3">
                          <FaFileAlt className="text-foreground text-xl" />
                        </div>
                        <span className="font-medium">Konsultacja</span>
                      </div>
                      
                      <p className="text-foreground-muted text-sm mb-6">
                        Umów się na konsultację online z naszym ekspertem IT. 
                        Omówimy potrzeby Twojej firmy i zaproponujemy proste, 
                        przystępne rozwiązania dopasowane do Twoich wymagań i budżetu.
                      </p>
                      
                      <div className="mt-auto">
                        <Divider className="my-4" />
                        <div className="flex flex-col gap-3">
                          <div className="flex items-center gap-2">
                            <FaCheckCircle className="text-success" />
                            <span className="text-sm">Bez zobowiązań</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <FaCheckCircle className="text-success" />
                            <span className="text-sm">Elastyczne terminy</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <FaCheckCircle className="text-success" />
                            <span className="text-sm">Bez technicznego żargonu</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default CtaSection;
